import moment from 'moment-timezone';
import { withLocalePrefix } from 'shared/utils/translation';
import _ from 'underscore';

import type { TimeZoneId, IanaTimeZoneId } from 'shared/components/calendars/types';
import type {
  FullCalendar,
  CalendarApiEvent,
  TimeStamp,
} from 'suggest_interview_times/types';
import type { Moment } from 'moment-timezone';

import { standardRequest } from 'shared/utils/request';

type EventGroup = {
  groupStartTime: Moment;
  groupEndTime: Moment;
  memberEvents: Array<CalendarApiEvent>;
};

type ViewRenderCallback = () => void;

const fullCalendarTranslations = withLocalePrefix('availability.full_calendar');

const startOfWeek = (fullCalendar: FullCalendar) => {
  return fullCalendar('getView').start.toISOString();
};

const onCalendarViewChange = (
  fullCalendar: FullCalendar,
  callback: ViewRenderCallback
) => {
  fullCalendar('option', 'viewRender', callback);
};

const setTimeZone = (fullCalendar: FullCalendar, timeZoneId: TimeZoneId) => {
  fullCalendar('option', 'timezone', timeZoneId);
};

const clearInterviewerEvents = (fullCalendar: FullCalendar) => {
  // @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
  fullCalendar('removeEvents', (e) => e.greenhouse_type === 'interviewer');
};

const buildFullCalendarEvent = (eventGroup: EventGroup) => {
  return {
    greenhouse_type: 'interviewer',
    allDay: false,
    start: eventGroup.groupStartTime.format(),
    end: eventGroup.groupEndTime.format(),
    memberEvents: eventGroup.memberEvents,
    background: true,
    editable: false,
    rendering: 'background',
    className: ['availability-interviewer-overlay'],
  };
};

const buildFullCalendarAllDayEvent = (event: any) => {
  return {
    greenhouse_type: 'interviewer',
    allDay: true,
    memberEvents: [event],
    start: event.starts_at.format(),
    end: event.ends_at.format(),
    background: true,
    editable: false,
    rendering: 'background',
    className: ['availability-interviewer-overlay'],
  };
};

const newEventGroup = (event: any): EventGroup => {
  return {
    groupStartTime: event.starts_at,
    groupEndTime: event.ends_at,
    memberEvents: [event],
  };
};

// @ts-expect-error - TS7006 - Parameter 'eventGroup' implicitly has an 'any' type.
const startTimeIntersects = (event: any, eventGroup) => {
  return event.starts_at.isBetween(
    eventGroup.groupStartTime,
    eventGroup.groupEndTime
  );
};

const groupIntersectingEvents = (
  accumulator: Array<EventGroup | any>,
  event: any
) => {
  const lastGroup = accumulator[accumulator.length - 1];

  if (!lastGroup || !startTimeIntersects(event, lastGroup)) {
    // Add the event as a new group
    accumulator.push(newEventGroup(event));
  } else {
    // Add the current event as a member of the last group
    lastGroup.groupEndTime = moment.max(event.ends_at, lastGroup.groupEndTime);
    lastGroup.memberEvents.push(event);
  }

  return accumulator;
};

const earliestStartTime = (event: any, otherEvent: any) => {
  return event.starts_at - otherEvent.starts_at;
};

const timeStampsToZonedMoments = (timezone: string) => {
  return (event: CalendarApiEvent) => {
    return {
      ...event,
      starts_at: moment(event.starts_at).tz(timezone),
      ends_at: moment(event.ends_at).tz(timezone),
    };
  };
};

// Events shorter than 15 minutes don't render well. This will expand short event groups to a length of 15 minutes
const expandShortGroups = (group: EventGroup) => {
  const { groupStartTime, groupEndTime } = group;
  if (groupEndTime.diff(groupStartTime, 'minutes') < 15) {
    return {
      ...group,
      groupEndTime: moment(groupStartTime.format()).add(15, 'minutes'),
    };
  } else {
    return group;
  }
};

const renderOnCalendar = (fullCalendar: FullCalendar) => {
  // @ts-expect-error - TS7006 - Parameter 'fullCalendarEvent' implicitly has an 'any' type.
  return (fullCalendarEvent) => {
    fullCalendar('renderEvent', fullCalendarEvent, true);
  };
};

const allDayOrScheduled = (event: {
  all_day: boolean;
  ends_at: TimeStamp | any;
  starts_at: TimeStamp | any;
  summary: string;
}) => (event.all_day ? 'allDayEvents' : 'scheduledEvents');

const renderInterviewerEvents = (
  fullCalendar: FullCalendar,
  events: Array<CalendarApiEvent>,
  timeZoneId: IanaTimeZoneId
) => {
  clearInterviewerEvents(fullCalendar);

  const allEvents = _.chain(events)
    .map(timeStampsToZonedMoments(timeZoneId))
    .groupBy(allDayOrScheduled);
  const { scheduledEvents = [], allDayEvents = [] } = allEvents.value();

  scheduledEvents
    .sort(earliestStartTime)
    .reduce<Array<any>>(groupIntersectingEvents, [])
    .map(expandShortGroups)
    .map(buildFullCalendarEvent)
    .concat(allDayEvents.map(buildFullCalendarAllDayEvent))
    .forEach(renderOnCalendar(fullCalendar));
};

const getFriendlyTimeZoneLabel = async (timeZoneId: TimeZoneId) => {
  if (timeZoneId) {
    const { data } = await standardRequest.get(
      `/time_zones/${encodeURIComponent(timeZoneId)}`
    );
    if (data && data.label) {
      return data.label;
    }
  }
  return `your local time zone (GMT${moment().format('Z')})`;
};

const setTimeZoneLabel = (timeZoneId: TimeZoneId) => {
  getFriendlyTimeZoneLabel(timeZoneId).then((friendlyLabel) => {
    const timeZoneText = fullCalendarTranslations('time_zone_label', {
      timeZone: friendlyLabel,
    });
    const existingLabel = document.querySelector('#availability_time_zone');

    if (existingLabel) {
      existingLabel.textContent = timeZoneText;
    } else {
      let newLabel = document.createElement('span');
      newLabel.setAttribute('id', 'availability_time_zone');
      newLabel.textContent = timeZoneText;
      let container = document.querySelector('.fc-left');
      if (container) {
        container.appendChild(newLabel);
      }
    }
  });
};

export {
  startOfWeek,
  renderInterviewerEvents,
  clearInterviewerEvents,
  onCalendarViewChange,
  setTimeZoneLabel,
  setTimeZone,
};
